// Ficha em digitação (issue #91): o que a Ficha da OS mostra ao montar e o que
// ela grava, sem DOM. A Ficha remonta do zero a cada mudança do Store; o que
// foi digitado e ainda não gravado vive aqui, na memória da página, e volta
// com UMA regra de precedência — memória > Rascunho do Fechamento gravado >
// Fechamento gravado. Antes eram sete lugares e três regras que discordavam
// (a Retida não salva sobrevivia à remontagem; a Recebida, não).

import { devolvidaEntregue, tiposBaixadosDoLote } from "../../domain/backlog.ts";
import { fechamentoDoLote } from "../../domain/fechamento-do-lote.ts";
import type { IntencaoFicha } from "../../domain/ficha-os.ts";
import type { Id } from "../../domain/ids.ts";
import { contagensDoLote, type DadosLote } from "../../domain/lotes.ts";
import type { Snapshot } from "../../domain/snapshot.ts";
import type { Comando } from "../../comandos/index.ts";

/** Uma peça como está na Ficha: Coleta (Recebida, Retida digitada) e Fechamento. */
export interface LinhaDigitada {
  recebida: number;
  retidaInformada: number | null;
  /** Total digitado em Devolvida (parte própria + carona do Backlog). */
  devolvida: number;
  retidas: { motivo: string; quantidade: number }[];
  /** `null` = sem divergência. */
  conferida: number | null;
  conferidaMotivo: string | null;
}

export interface DevolucaoDigitada {
  data: string;
  hora: string;
}

/**
 * O cabeçalho da Coleta como está nos campos — texto cru, não `DadosLote`:
 * um número de OS apagado é "" (não 0) e um "prazo diferente" marcado antes
 * de ter data e hora continua marcado.
 */
export interface CabecalhoDigitado {
  clienteId: string;
  paradaId: string;
  coletaData: string;
  coletaHora: string;
  os: string;
  sacos: string;
  urgente: boolean;
  relavagem: boolean;
  temPrazo: boolean;
  prazoData: string;
  prazoHora: string;
}

/** O que foi digitado numa OS e ainda não gravado — cada parte só se tocada. */
export interface FichaEmDigitacao {
  /** A OS no banco quando se começou a digitar (ver `fotoDaOS`). */
  base: string;
  cabecalho?: CabecalhoDigitado;
  devolucao?: DevolucaoDigitada;
  linhas: Map<Id, LinhaDigitada>;
}

/** A chave da Nova OS (ainda sem Lote) na memória. */
export const NOVA_OS = "nova-os";

/**
 * Uma "foto" do que está gravado da OS: o Lote, as contagens, as retenções e
 * as baixas dela. Se mudou desde que se começou a digitar — outro computador
 * salvou, ou o próprio "Salvar" gravou —, a memória fica velha e é descartada:
 * o que está no banco vence.
 */
export function fotoDaOS(snap: Snapshot, loteId: Id): string {
  if (loteId === NOVA_OS) return "";
  return JSON.stringify([
    snap.lotes.find((l) => l.id === loteId) ?? null,
    snap.contagens.filter((c) => c.loteId === loteId),
    snap.retencoes.filter((r) => r.loteId === loteId),
    snap.baixasBacklog.filter((b) => b.loteId === loteId),
    snap.baixasBacklogInicial.filter((b) => b.loteId === loteId),
  ]);
}

/** A memória da página: por OS, o que foi digitado e ainda não gravado. */
export class MemoriaDaFicha {
  private readonly porLote = new Map<Id, FichaEmDigitacao>();

  /** O que foi digitado nesta OS — só se o banco não mudou desde então. */
  de(snap: Snapshot, loteId: Id): FichaEmDigitacao | undefined {
    const f = this.porLote.get(loteId);
    if (f && f.base !== fotoDaOS(snap, loteId)) {
      this.porLote.delete(loteId);
      return undefined;
    }
    return f;
  }
  private da(snap: Snapshot, loteId: Id): FichaEmDigitacao {
    let f = this.de(snap, loteId);
    if (!f) this.porLote.set(loteId, (f = { base: fotoDaOS(snap, loteId), linhas: new Map() }));
    return f;
  }
  /** Uma peça por vez: lembrar uma nunca apaga as outras (OS 348). */
  lembrarLinha(snap: Snapshot, loteId: Id, tipoId: Id, linha: LinhaDigitada): void {
    this.da(snap, loteId).linhas.set(tipoId, { ...linha, retidas: linha.retidas.map((r) => ({ ...r })) });
  }
  lembrarDevolucao(snap: Snapshot, loteId: Id, devolucao: DevolucaoDigitada): void {
    this.da(snap, loteId).devolucao = { ...devolucao };
  }
  lembrarCabecalho(snap: Snapshot, loteId: Id, cabecalho: CabecalhoDigitado): void {
    this.da(snap, loteId).cabecalho = { ...cabecalho };
  }
  /** Ao começar uma Nova OS do zero, ou ao desistir dela. */
  esquecer(loteId: Id): void {
    this.porLote.delete(loteId);
  }
}

/** A memória desta página (a Ficha é remontada, a página não). */
export const memoriaDaFicha = new MemoriaDaFicha();

export interface ValoresIniciais {
  cabecalho: CabecalhoDigitado;
  /** `null`: nada gravado nem digitado — a Ficha sugere o previsto. */
  devolucao: DevolucaoDigitada | null;
  /** As peças com algo gravado ou digitado; as demais começam vazias. */
  linhas: Map<Id, LinhaDigitada>;
}

const vazia = (): LinhaDigitada => ({
  recebida: 0, retidaInformada: null, devolvida: 0, retidas: [], conferida: null, conferidaMotivo: null,
});

/**
 * O que a Ficha da OS mostra ao montar, por uma regra só, peça a peça:
 * memória da página > Rascunho do Fechamento gravado > Fechamento gravado.
 * A Coleta (Recebida, Retida digitada) do rascunho vem da contagem gravada —
 * o rascunho só guarda o Fechamento. Memória feita sobre outra versão da OS
 * no banco não vale (`MemoriaDaFicha.de`).
 */
export function valoresIniciais(snap: Snapshot, loteId: Id, memoria?: FichaEmDigitacao): ValoresIniciais {
  const lote = snap.lotes.find((l) => l.id === loteId);
  if (!lote) throw new Error("Lote não encontrado.");
  const contagens = new Map(contagensDoLote(snap, loteId).map((c) => [c.tipoId, c]));
  const retidasGravadas = new Map<Id, { motivo: string; quantidade: number }[]>();
  for (const r of snap.retencoes) {
    if (r.loteId !== loteId) continue;
    retidasGravadas.set(r.tipoId, [...(retidasGravadas.get(r.tipoId) ?? []), { motivo: r.motivo, quantidade: r.quantidade }]);
  }
  const rascunho = new Map((lote.rascunhoFechamento?.linhas ?? []).map((l) => [l.tipoId, l]));

  const tipos = new Set<Id>([
    ...contagens.keys(),
    ...retidasGravadas.keys(),
    ...tiposBaixadosDoLote(snap, loteId),
    ...rascunho.keys(),
    ...(memoria?.linhas.keys() ?? []),
  ]);
  const linhas = new Map<Id, LinhaDigitada>();
  for (const tipoId of tipos) {
    const lembrada = memoria?.linhas.get(tipoId);
    if (lembrada) {
      linhas.set(tipoId, { ...lembrada, retidas: lembrada.retidas.map((r) => ({ ...r })) });
      continue;
    }
    const c = contagens.get(tipoId);
    const coleta = { recebida: c?.recebida ?? 0, retidaInformada: c?.retidaInformada ?? null };
    const r = rascunho.get(tipoId);
    if (r) {
      linhas.set(tipoId, {
        ...coleta,
        devolvida: r.devolvida,
        retidas: r.retidas.map((x) => ({ ...x })),
        conferida: r.conferida,
        conferidaMotivo: r.conferidaMotivo,
      });
      continue;
    }
    linhas.set(tipoId, {
      ...vazia(),
      ...coleta,
      // o total entregue (parte própria + carona do Backlog) é o que foi digitado
      devolvida: devolvidaEntregue(snap, loteId, tipoId),
      retidas: retidasGravadas.get(tipoId) ?? [],
      conferida: c?.conferida ?? null,
      conferidaMotivo: c?.conferidaMotivo ?? null,
    });
  }

  const fechamento = fechamentoDoLote(lote);
  const devolucao: DevolucaoDigitada | null =
    memoria?.devolucao ??
    (fechamento ? { data: fechamento.data, hora: fechamento.hora ?? "" } : null) ??
    (lote.rascunhoFechamento ? { data: lote.rascunhoFechamento.data, hora: lote.rascunhoFechamento.hora ?? "" } : null);

  const [prazoData = "", prazoHora = ""] = lote.previstoManual?.split(" ") ?? [];
  const cabecalho: CabecalhoDigitado = memoria?.cabecalho ?? {
    clienteId: lote.clienteId,
    paradaId: lote.paradaId ?? "",
    coletaData: lote.coletaData,
    coletaHora: lote.coletaHora,
    os: String(lote.os),
    sacos: lote.sacos == null ? "" : String(lote.sacos),
    urgente: lote.urgente,
    relavagem: lote.relavagem,
    temPrazo: lote.previstoManual != null,
    prazoData,
    prazoHora,
  };
  return { cabecalho, devolucao, linhas };
}

/** O cabeçalho digitado como `DadosLote` — o que vai no comando. */
export function dadosDoCabecalho(c: CabecalhoDigitado): DadosLote {
  return {
    clienteId: c.clienteId,
    os: Number(c.os),
    coletaData: c.coletaData,
    coletaHora: c.coletaHora,
    paradaId: c.paradaId || null,
    sacos: c.sacos.trim() === "" ? null : Number(c.sacos),
    previstoManual: c.temPrazo && c.prazoData && c.prazoHora ? `${c.prazoData} ${c.prazoHora}` : null,
    urgente: c.urgente,
    relavagem: c.relavagem,
  };
}
/**
 * O comando "gravar a Ficha" (#91) com o que está digitado: a Comanda inteira
 * (todas as peças) e o Fechamento só das peças com algo nele.
 */
export function comandoGravarFicha(
  loteId: Id,
  ficha: { dados: DadosLote; devolucao: DevolucaoDigitada; linhas: ReadonlyMap<Id, LinhaDigitada> },
  intencao: IntencaoFicha,
  reabrirFechamento: boolean,
): Comando {
  const linhas = [...ficha.linhas.entries()];
  return {
    agregado: "lote",
    cmd: {
      tipo: "gravarFicha",
      id: loteId,
      intencao,
      dados: ficha.dados,
      reabrirFechamento,
      recebidas: linhas.map(([tipoId, l]) => ({ tipoId, quantidade: l.recebida, retidaInformada: l.retidaInformada })),
      fechamento: {
        data: ficha.devolucao.data,
        ...(ficha.devolucao.hora ? { hora: ficha.devolucao.hora } : {}),
        linhas: linhas
          .filter(([, l]) => l.devolvida > 0 || l.retidas.length > 0 || l.conferida != null)
          .map(([tipoId, l]) => ({
            tipoId,
            devolvida: l.devolvida,
            retidas: l.retidas.map((r) => ({ ...r })),
            ...(l.conferida != null ? { conferida: l.conferida } : {}),
            ...(l.conferida != null && l.conferidaMotivo ? { conferidaMotivo: l.conferidaMotivo } : {}),
          })),
      },
    },
  };
}
